import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Star, MessageSquare, Loader2, User, Search } from 'lucide-react';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import Avatar from '@components/common/Avatar';
import mentorService from '@services/mentor.service';
import toast from 'react-hot-toast';

const MentorsList = () => {
  const navigate = useNavigate();
  const [allMentors, setAllMentors] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const mentorsPerPage = 12;
  const observerTarget = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filterMentorsLocally = useCallback((searchQuery) => {
    if (!searchQuery || searchQuery.length === 0) {
      setMentors(allMentors);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = allMentors.filter(mentor => {
      const fullName = `${mentor.firstName || ''} ${mentor.lastName || ''}`.toLowerCase().trim();
      const name = (mentor.name || '').toLowerCase();
      const position = (mentor.currentPosition || '').toLowerCase();
      const expertise = (mentor.expertise || []).join(' ').toLowerCase();
      const bio = (mentor.bio || '').toLowerCase();
      
      return fullName.includes(query) ||
             name.includes(query) ||
             position.includes(query) ||
             expertise.includes(query) ||
             bio.includes(query);
    });

    setMentors(filtered);
  }, [allMentors]);

  useEffect(() => {
    if (debouncedSearchTerm) {
      filterMentorsLocally(debouncedSearchTerm);
    } else {
      setMentors(allMentors);
    }
  }, [debouncedSearchTerm, allMentors, filterMentorsLocally]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          setCurrentPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, loadingMore]);

  const fetchMentors = useCallback(async () => {
    try {
      if (currentPage === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params = {
        page: currentPage,
        limit: mentorsPerPage,
      };

      const response = await mentorService.getAllMentors(params);
      
      if (response.success) {
        const responseData = response.data?.data || response.data;
        const mentorData = responseData.mentors || [];
        const totalPagesFromAPI = responseData.pagination?.totalPages || 1;

        if (currentPage === 1) {
          setAllMentors(mentorData);
          setMentors(mentorData);
        } else {
          setAllMentors((prev) => [...prev, ...mentorData]);
          setMentors((prev) => [...prev, ...mentorData]);
        }

        setTotalPages(totalPagesFromAPI);
        setHasMore(currentPage < totalPagesFromAPI);
      } else {
        if (currentPage === 1) {
          setAllMentors([]);
          setMentors([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch mentors:', error);
      toast.error('Failed to load mentors');
      if (currentPage === 1) {
        setAllMentors([]);
        setMentors([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [currentPage, mentorsPerPage]);

  useEffect(() => {
    if (hasMore) {
      fetchMentors();
    }
  }, [currentPage, hasMore, fetchMentors]);

  return (
    <div className="space-y-4 max-w-full overflow-hidden">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 max-w-full overflow-hidden">
        <div className="flex items-center gap-2 min-w-0 flex-1 mb-3">
          <div className="w-8 h-8 flex items-center justify-center bg-secondary-50 rounded-lg flex-shrink-0">
            <Users className="w-4 h-4 text-secondary-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-primary-700 leading-tight truncate">
              Mentors Dashboard
            </h2>
            <p className="text-xs text-gray-600 truncate">
              Browse all mentors in the platform
            </p>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, position, or expertise..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          {searchTerm && (
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm('');
                setDebouncedSearchTerm('');
              }}
              className="sm:w-auto"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Mentor Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-secondary-500 animate-spin" />
        </div>
      ) : mentors.length > 0 ? (
        <>
          <div
            className="grid gap-3 sm:gap-4"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 230px))' }}
          >
            {mentors.map((mentor) => (
            <Card
              key={mentor.id}
              className="shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-secondary-400 transition-all duration-200 p-4 flex flex-col"
            >
              <div className="flex flex-col items-center flex-1 w-full min-w-0">
                {/* Avatar */}
                <div className="mb-3">
                  <Avatar
                    src={mentor.profilePicture}
                    name={`${mentor.firstName || ''} ${mentor.lastName || ''}`.trim() || mentor.name}
                    size="xl"
                    showBorder={false}
                  />
                </div>

                {/* Name */}
                <h3 className="font-bold text-primary-700 mb-1 text-sm text-center line-clamp-1 w-full px-1">
                  {mentor.firstName && mentor.lastName 
                    ? `${mentor.firstName} ${mentor.lastName}` 
                    : mentor.name || 'Mentor'}
                </h3>

                {/* Current Position */}
                {mentor.currentPosition && (
                  <p className="text-xs font-semibold text-secondary-600 mb-2 text-center line-clamp-2 w-full px-1">
                    {mentor.currentPosition}
                  </p>
                )}

                {/* Years of Experience */}
                {mentor.yearsOfExperience && (
                  <div className="flex items-center gap-1 text-xs text-primary-600 mb-2">
                    <span className="font-medium">{mentor.yearsOfExperience} years experience</span>
                  </div>
                )}

                {/* Rating */}
                <div className="flex items-center justify-center gap-1.5 mb-3 bg-yellow-50 px-2.5 py-1 rounded-full border border-yellow-200">
                  <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                  <span className="text-xs font-bold text-primary-700">
                    {mentor.averageRating || 'N/A'}
                  </span>
                  {mentor.totalRatings > 0 && (
                    <span className="text-xs text-gray-600">({mentor.totalRatings})</span>
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center justify-center gap-3 text-xs text-gray-600 mb-3">
                  <div className="flex items-center gap-1">
                    <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{mentor.totalQuestions || 0} questions</span>
                  </div>
                </div>

                {/* View Profile Button */}
                <div className="w-full mt-auto">
                  <Button 
                    variant="outline" 
                    className="w-full text-xs px-3 py-2"
                    onClick={() => navigate(`/mentor-profile/${mentor.id}`, { state: { from: 'mentorsList' } })}
                  >
                    View Profile
                  </Button>
                </div>
              </div>
            </Card>
            ))}
          </div>

          {/* Infinite Scroll Trigger */}
          <div ref={observerTarget} className="w-full py-4">
            {loadingMore && (
              <div className="flex justify-center items-center gap-2">
                <div className="w-6 h-6 border-3 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-primary-600 font-medium">Loading more mentors...</span>
              </div>
            )}
            {!hasMore && mentors.length >= mentorsPerPage && (
              <div className="text-center text-gray-500">
                <p>You've reached the end of the list</p>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-16">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-primary-700 mb-2">
            No Mentors Available
          </h3>
          <p className="text-gray-600">
            Check back later for available mentors
          </p>
        </div>
      )}
    </div>
  );
};

export default MentorsList;